// api/post-production.ts — the Finish & Post calls. (session 8, F1) Run-keyed, and routed by the run's tenant: a vms:covers
// run is finished by the ENGINE (the console, with the operator's key); TubeForge's own runs by the backend, as before.
import { apiGet, apiPost } from "./client.ts";
import { engineGet, enginePost } from "./engine-console.ts";

/** Who serves the call: the engine console (a vms:covers run) or the backend (TubeForge's own). */
export type FinishingVia = "engine" | "app";

export interface CorrectionResult {
  stationId: string;
  stage: "correction";
  passed: boolean;
  defect?: string;
  evidence?: string;
  beatId?: string;
}

export interface CorrectionChecklist {
  runId: string | null;
  results: CorrectionResult[];
  allPassed: boolean;
  pending: boolean;
  /** True when no post_production run exists yet for the clone. */
  notStarted?: boolean;
}

// ── RUN-KEYED (the T4 completed-jobs handoff) ────────────────────────────────
/** The checklist for a PRODUCTION run (resolves the post_production run server-side). */
export const getPostProductionChecklistByRun = (productionRunId: string, signal?: AbortSignal, via: FinishingVia = "app") =>
  via === "engine"
    ? engineGet<CorrectionChecklist>(`/covers/post-production/runs/${productionRunId}`)
    : apiGet<CorrectionChecklist>(`/covers/admin/post-production/runs/${productionRunId}`, signal);

/** Create the post_production run for a production run (the "Send to post-production" trigger). */
/** `callback: "growth"` (Phase 4b): the growth item learns its finished URL when finishing completes — the backend
 *  resolves it to its own GROWTH_CALLBACK_URL. Omitted (covers) → exactly as before. */
export const runPostProductionByRun = (productionRunId: string, params?: Record<string, unknown>, callback?: "growth", via: FinishingVia = "app") =>
  via === "engine"
    ? enginePost<{ ok: true; postProductionRunId: string }>(`/covers/post-production/runs/${productionRunId}/run`, params ? { params } : {})
    : apiPost<{ ok: true; postProductionRunId: string }>(
      `/covers/admin/post-production/runs/${productionRunId}/run`,
      params || callback ? { ...(params ? { params } : {}), ...(callback ? { callback } : {}) } : undefined,
    );

/** Approve the finishing run's waiting gate (correction, then delivery — one per call). Audit fix: "Approve & Post"
 *  had no handler. */
export const approvePostProductionByRun = (productionRunId: string, via: FinishingVia = "app") =>
  via === "engine"
    ? enginePost<{ ok: true; postProductionRunId: string; approvedGate: string }>(`/covers/post-production/runs/${productionRunId}/approve`, {})
    : apiPost<{ ok: true; postProductionRunId: string; approvedGate: string }>(
      `/covers/admin/post-production/runs/${productionRunId}/approve`, {},
    );

/** (tf3-27; creative C1.6b handoff §1.1) the delivery gate's platform versions: the master and one version per platform
 *  of the newest finishing run — its state (a refused one says why), checks as measured, cover frame, measured length and
 *  words. `runId`: the production run (or a finishing run). 404 → no finishing run yet. */
export const getPlatformVersionsByRun = (runId: string, via: FinishingVia = "app", signal?: AbortSignal) =>
  via === "engine"
    ? engineGet<import("../lib/platform-versions.ts").VersionsView>(`/covers/post-production/runs/${runId}/versions`)
    : apiGet<import("../lib/platform-versions.ts").VersionsView>(`/covers/admin/post-production/runs/${runId}/versions`, signal);

// ── P4.2 — library catalogue for the music/sfx pickers ───────────────────────
export interface CatalogueItem { id: string; type: string; name: string }
/** List library items of the given types (e.g. "music_style", "sound_effect"). */
export const getLibraryCatalogue = (types: string[], signal?: AbortSignal) =>
  apiGet<{ items: CatalogueItem[] }>(
    `/covers/admin/library/catalogue?types=${encodeURIComponent(types.join(","))}`,
    signal,
  );
